import { clsx } from "clsx";
import { Check } from "lucide-react";
import { AnimatePresence, m, type HTMLMotionProps } from "motion/react";
import { forwardRef, type ReactNode } from "react";

import styles from "./Button.module.css";
import { Spinner } from "./Spinner";

export type ButtonProps = Omit<HTMLMotionProps<"button">, "children"> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  done?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
};

/**
 * Buttons press down slightly, a sheen sweeps across on hover, and the icon slot morphs
 * between the icon, a spinner while loading and a check when done, without the width jumping.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    done = false,
    icon,
    iconRight,
    children,
    className,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const state = loading ? "loading" : done ? "done" : "idle";
  const slot = loading ? <Spinner size={16} /> : done ? <Check size={16} strokeWidth={2.6} /> : icon;
  return (
    <m.button
      ref={ref}
      type={type}
      className={clsx(styles.button, styles[variant], styles[size], className)}
      data-state={state}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      whileTap={disabled || loading ? undefined : { scale: 0.97 }}
      transition={{ type: "spring", stiffness: 600, damping: 30 }}
      {...rest}
    >
      <span className={styles.sheen} aria-hidden="true" />
      {slot ? (
        <span className={styles.icon} aria-hidden="true">
          <AnimatePresence mode="popLayout" initial={false}>
            <m.span
              key={state}
              className={styles.iconInner}
              initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.6 }}
              transition={{ duration: 0.18 }}
            >
              {slot}
            </m.span>
          </AnimatePresence>
        </span>
      ) : null}
      {children ? <span className={styles.label}>{children}</span> : null}
      {iconRight ? (
        <span className={clsx(styles.icon, styles.iconRight)} aria-hidden="true">
          {iconRight}
        </span>
      ) : null}
    </m.button>
  );
});

/** Class names for a link that should look like a button (a link must not wrap a button). */
export function buttonClass(
  variant: NonNullable<ButtonProps["variant"]> = "secondary",
  size: NonNullable<ButtonProps["size"]> = "md",
): string {
  return clsx(styles.button, styles[variant], styles[size], styles.link);
}

